import {isEmpty} from 'lodash';
import React, {FC} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LabeledInputDropdown} from '../../common/dropdowns/labeledInputDropdown';
import {BaseFilterDropdownProps} from '../../common/filters/common/types';
import {
  LensFormAssigneesModalFilterDropdown,
  renderAssigneeSelectionValue,
} from './lensFormAssigneesModalFilterDropdown';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormMultiSelectExcludeAssigneesProps<ResourceType extends {id: number}>
  extends LensFormMultiselectProps<TeammateModel> {
  selectedInboxIds?: ReadonlyArray<number>;
  shouldFilterTeammatesByInboxAccess?: boolean;
  selectedInboxesInState: ReadonlyArray<InboxModel>;
  selectedExcludedIds: ReadonlyArray<ResourceType['id']>;
  onSetExcludedItemIds: (arg: ReadonlyArray<ResourceType['id']>) => void;
}

/*
 * Component.
 */

export const LensFormMultiSelectExcludeAssignees: FC<
  LensFormMultiSelectExcludeAssigneesProps<TeammateModel>
> = (props) => {
  const {
    isDisabled,
    choices,
    selectedIds,
    onSetItemIds,
    selectedExcludedIds,
    onSetExcludedItemIds,
    selectedInboxIds,
    selectedInboxesInState,
    shouldFilterTeammatesByInboxAccess,
  } = props;
  const theme = useUserTheme();

  const renderModalDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <LensFormAssigneesModalFilterDropdown
      choices={choices}
      selectedIds={selectedIds}
      onSetItemIds={onSetItemIds}
      excludedSelectedIds={selectedExcludedIds}
      onSetExcludedItemIds={onSetExcludedItemIds}
      selectedInboxIds={selectedInboxIds}
      selectedInboxesInState={selectedInboxesInState}
      shouldFilterTeammatesByInboxAccess={shouldFilterTeammatesByInboxAccess}
      anchor={dropdownProps.anchor}
      onRequestClose={dropdownProps.onRequestClose}
    />
  );

  return (
    <LabeledInputDropdown
      renderDropdown={renderModalDropdown}
      renderValue={() => renderValue(selectedIds, selectedExcludedIds, choices, theme)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-exclude-tags-placeholder">Select teammates</Trans>
      )}
      label={
        props.label || (
          <Trans id="lens-form-multiselect-exclude-tags-label">Filtering by any of these assignees</Trans>
        )
      }
      isDisabled={isDisabled}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">
          Field will be enabled once an inbox is selected.
        </Trans>
      }
      hasError={props.hasError ?? false}
      errorMessage={props.errorMessage}
      hasPreviousSelection={!isEmpty(selectedIds) || !isEmpty(selectedExcludedIds)}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(
  selectedIds: ReadonlyArray<number>,
  excludedSelectedIds: ReadonlyArray<number>,
  choices: ReadonlyArray<TeammateModel>,
  theme: UserTheme,
) {
  if (!selectedIds.length && !excludedSelectedIds.length) {
    return null;
  }

  if (excludedSelectedIds.length) {
    return renderAssigneeSelectionValue(excludedSelectedIds, true, choices, theme);
  }

  return renderAssigneeSelectionValue(selectedIds, false, choices, theme);
}
